"use client";

/**
 * Height-reserving placeholder for a deferred BirthDetailsForm-based tool
 * island (mirrors the fields BirthDetailsForm renders: name, birth date,
 * birth time, birth place + submit). Rendered in two situations:
 *
 * 1. Pre-activation — as the interaction target that triggers loading the
 *    real chunk (`onActivate`), so the tool stays in the first viewport
 *    (rules 43/61) without shipping the heavy form stack in the route's
 *    initial JS.
 * 2. While the dynamic chunk loads (`next/dynamic` `loading:`) — same
 *    markup minus the activation handlers, so the swap never collapses to
 *    0px (rule 58 — never `loading: () => null` for a form/result surface).
 */
export default function BirthToolFormShell({
  language = "en",
  submitLabel,
  onActivate,
}: {
  language?: "en" | "hi";
  submitLabel?: string;
  onActivate?: () => void;
}) {
  const copy =
    language === "hi"
      ? {
          name: "Naam",
          birthDate: "Janam ki date",
          birthTime: "Janam ka time",
          birthPlace: "Janam ka place",
          submit: "Details bharein",
        }
      : {
          name: "Your Name",
          birthDate: "Birth Date",
          birthTime: "Birth Time",
          birthPlace: "Birth Place",
          submit: "Continue",
        };

  return (
    <div className="mx-auto max-w-md" onPointerDown={onActivate} onFocus={onActivate}>
      <form
        aria-busy="true"
        className="card-sacred min-h-[420px] p-6 sm:p-8 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate?.();
        }}
      >
        <ShellField label={copy.name} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ShellField label={copy.birthDate} />
          <ShellField label={copy.birthTime} />
        </div>
        <ShellField label={copy.birthPlace} />
        <button
          type="submit"
          className="btn-sacred w-full rounded-lg py-3.5 text-[14px] font-semibold"
        >
          {submitLabel ?? copy.submit} &rarr;
        </button>
      </form>
    </div>
  );
}

function ShellField({ label }: { label: string }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
        {label}
      </span>
      <span
        className="block h-11 rounded-lg"
        style={{
          background: "var(--ish-bg)",
          border: "1px solid var(--ish-border)",
        }}
      />
    </label>
  );
}
